<script>
import { Helper } from '@/mixin/helper'
import RestEditableTableV2 from '@/components/RestEditableTableV2'
import * as vehicleTypeApi from '@/services/rest-api-sms/lib/vehicle/vehicle-type'
import * as vehicleCategoryApi from '@/services/rest-api-sms/lib/vehicle/vehicle-category'
import * as vehicleModelApi from '@/services/rest-api-sms/lib/vehicle/vehicle-model'
import * as fuelTypeApi from '@/services/rest-api-sms/lib/fuel-type/fuel-type'

export default {
  components: {
    RestEditableTableV2,
  },

  mixins: [Helper],

  data() {
    return {
      isTableDataAvailable: true,
    }
  },

  computed: {
    /**
     * Total no. of records
     */
    title() {
      return 'Jenis Kendaraan'
    },

    fields() {
      return [
        {
          key: 'type',
          thClass: 'mc-table--170',
          editable: true,
          label: this.$t('task.column.vehicleType'),
          placeholder: 'Jenis Kendaraan',
          validation: 'required|max:64,length',
          type: 'text',
          'validation-messages': {
            required: this.$t('global.validation.required'),
            max: 'Jenis kendaraan tidak lebih dari 64 karakter',
          },
        },

        {
          key: 'manufacturer',
          thClass: 'mc-table--170',
          editable: true,
          label: this.$t('master.list.fleetType.manufacturer'),
          placeholder: this.$t('master.list.fleetType.manufacturer'),
          validation: 'required',
          type: 'select-resourceful',
          trackBy: 'model',
          optionsGetter: this.queries.vehicleModels,
          optionLabelFn: ({ item }) => item.model,
          getInitialObject: (item) => {
            return { model: item.manufacturer }
          },
          'validation-messages': {
            required: this.$t('global.validation.required'),
            max: 'Nama manufakturer tidak lebih dari 64 karakter',
          },
          customLabelFn: (item, key = 'manufacturer') => `${item[key]} - ${item.model}`,
        },

        {
          key: 'category',
          thClass: 'mc-table--170',
          editable: true,
          label: this.$t('master.form.fleet.fleetCategory'),
          placeholder: this.$t('master.form.fleet.fleetCategory'),
          validation: 'required',
          type: 'select-resourceful',
          trackBy: 'category',
          optionsGetter: this.queries.vehicleCategories,
          getInitialObject: (item) => {
            return { category: item.category }
          },
          optionLabelFn: ({ item }) => item.category,
          'validation-messages': {
            required: this.$t('global.validation.required'),
            max: 'Nama kategori tidak lebih dari 64 karakter',
          },
          customLabelFn: (item, key = 'category') =>
            item[`${key}`] || item[`${key}`] !== '' ? item[`${key}`] : '-',
        },

        {
          key: 'tonnage',
          thClass: 'mc-table--150',
          editable: true,
          label: this.$t('master.list.fleetType.tonnage'),
          placeholder: this.$t('master.list.fleetType.tonnage'),
          _type: 'number',
        },

        {
          key: 'volume',
          thClass: 'mc-table--130',
          editable: true,
          label: this.$t('master.form.fleet.volume'),
          placeholder: this.$t('master.form.fleet.volume'),
          _type: 'number',
        },

        {
          key: 'fuel_efficiency',
          thClass: 'mc-table--150',
          editable: true,
          label: this.$t('master.list.fleetType.fuelEfficiency'),
          placeholder: this.$t('master.list.fleetType.fuelEfficiency'),
          _type: 'number',
          customLabelFn: (item, key = 'fuel_efficiency') => item[`${key}`] ?? 0,
          validation: 'number|min:0,value|max:6,length',
          'validation-messages': {
            number: 'Invalid input',
            min: 'Minimum berat kosong tidak kurang dari 0 Km/L',
          },
        },

        {
          key: 'fuel_type_name',
          thClass: 'mc-table--170',
          editable: true,
          label: this.$t('liveView.infoWindow.fuel'),
          placeholder: this.$t('liveView.infoWindow.fuel'),
          type: 'select-resourceful',
          trackBy: 'name',
          optionsGetter: this.queries.fuelType,
          optionLabelFn: ({ item }) => item.name,
          getInitialObject: (item) => {
            return { name: item.fuel_type }
          },
          validation: [['required']],
        },

        {
          key: 'transmission_type',
          thClass: 'mc-table--130',
          thStyle: 'width: 300px',
          editable: true,
          label: 'Transmisi',
          placeholder: 'Pilih Transmisi',
          type: 'select-resourceful',
          trackBy: 'transmission_type',
          optionsGetter: this.queries.transmissionType,
          optionLabelFn: ({ item }) => item.transmission_type,
          getInitialObject: (item) => {
            return { transmission_type: item.transmission_type }
          },
          customValueFn: (item) => item.transmission_type ?? '',
          validation: [['required']],
        },
        {
          key: 'icon',
          editable: true,
          thClass: 'd-none',
          tdClass: 'd-none',
        },
        {
          key: 'company_id',
          editable: true,
          thClass: 'd-none',
          tdClass: 'd-none',
        },
      ]
    },
    apiClient() {
      return {
        get: vehicleTypeApi.get,
        show: vehicleTypeApi.show,
        store: vehicleTypeApi.store,
        delete: vehicleTypeApi.deleteMany,
        update: vehicleTypeApi.update,
      }
    },

    queries() {
      return {
        vehicleModels: async (opts) => {
          const params = {
            search: opts?.search,
            limit: opts?.limit,
            page: opts?.page,
          }
          const result = await vehicleModelApi.get(params)
          // Manipulasi data agar model berformat "manufacturer - model"
          if (result?.data?.paginated_result) {
            result.data.paginated_result = result.data.paginated_result.map((item) => ({
              ...item,
              model: `${item.manufacturer} - ${item.model}`,
            }))
          }
          return result
        },

        vehicleCategories: async (opts) => {
          const params = {
            search: opts?.search,
            limit: opts?.limit,
            page: opts?.page,
          }
          const result = await vehicleCategoryApi.get(params)
          return result
        },

        fuelType: async (opts) => {
          const params = {
            search: opts?.search,
            limit: opts?.limit,
            page: opts?.page,
          }
          const result = await fuelTypeApi.get(params)
          return result
        },

        transmissionType: async (opts) => {
          let transmissionTypeOptions = vehicleTypeApi.constants.data.transmissionTypeOptions
          if (opts.search) {
            transmissionTypeOptions = transmissionTypeOptions.filter((item) =>
              this.isContains(opts.search, item.transmission_type)
            )
          }
          const result = {
            metadata: {
              total_page: 1,
              total_count: transmissionTypeOptions.length,
              page: 1,
            },
            data: {
              paginated_result: transmissionTypeOptions,
            },
          }
          return result
        },
      }
    },
  },

  methods: {
    handleDataAvailable(isAvailable) {
      this.isTableDataAvailable = isAvailable
    },
    isContains(search, name) {
      const searchContains = RegExp(search ?? '', 'gi').exec(name ?? '')
      return searchContains != null && searchContains.length > 0
    },
    async handleChange() {
      const fleetTypeRef = this.$refs.fleetTypeRef.$refs
      await this.$nextTick()
      this.fields.forEach((field) => {
        if (!field.editable) return
        fleetTypeRef[`${field.key}-ref`].$el.addEventListener(
          'keydown',
          Helper.methods.preventTabKey
        )
      })
    },
  },
}
</script>

<template>
  <div>
    <div class="card">
      <div class="card-body">
        <RestEditableTableV2
          ref="fleetTypeRef"
          v-show="isTableDataAvailable"
          searchable
          :fields="fields"
          :apiClient="apiClient"
          :bulkDeleteApi="true"
          @available-change="handleDataAvailable"
          trackBy="vehicle_type_id"
          @changeData="handleChange"
        />
      </div>
    </div>
  </div>
</template>
